import React from "react";
import { motion } from "framer-motion";
import { certifications } from "../portfolio";
import { fadeIn, blurIn, staggerContainer } from "../utils/motion";
import { FiArrowUpRight } from "react-icons/fi";
import AcademicManualsShowcase from "./AcademicManualsShowcase";

function CertBadge({ cert, index }) {
  const logoPath = (() => {
    try {
      return require(`../assests/images/${cert.logo_path}`);
    } catch {
      return null;
    }
  })();

  const isClickable = Boolean(
    cert.certificate_link && cert.certificate_link.trim()
  );
  const Tag = isClickable ? "a" : "div";
  const tagProps = isClickable
    ? {
        href: cert.certificate_link,
        target: "_blank",
        rel: "noopener noreferrer",
      }
    : {};

  return (
    <motion.div variants={fadeIn("up", index * 0.05)}>
      <Tag
        {...tagProps}
        className={`group relative flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl border border-surface/[0.06] bg-surface/[0.015] transition-all duration-300 ${
          isClickable
            ? "hover:border-surface/[0.16] hover:bg-surface/[0.04] cursor-pointer"
            : "cursor-default"
        }`}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {logoPath && (
            <img
              src={logoPath}
              alt={cert.alt_name}
              className="w-7 h-7 rounded-md object-contain shrink-0 bg-surface/[0.03] p-0.5 border border-surface/[0.04]"
            />
          )}
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-medium text-fg-muted group-hover:text-fg transition-colors duration-300 break-words leading-tight">
              {cert.title}
            </p>
            <p className="text-[11px] font-mono text-fg-faint mt-0.5">
              {cert.subtitle}
            </p>
          </div>
        </div>

        {isClickable && (
          <span className="shrink-0 text-fg-ghost group-hover:text-fg transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5">
            <FiArrowUpRight size={14} />
          </span>
        )}
      </Tag>
    </motion.div>
  );
}

function Education() {
  return (
    <motion.div
      variants={staggerContainer()}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.1 }}
    >
      <motion.div variants={blurIn("up")} className="mb-8">
        <span className="section-label">Background</span>
        <h2 className="section-title">Education</h2>
      </motion.div>

      {/* 3D Academic Manuals Showcase */}
      <motion.div variants={fadeIn("up", 0.1)}>
        <AcademicManualsShowcase />
      </motion.div>

      <motion.div variants={fadeIn("up", 0.2)}>
        <h3 className="font-mono text-xs tracking-[0.15em] uppercase text-fg-faint mb-8">
          Certifications
        </h3>

        {certifications.sections ? (
          <div className="space-y-8">
            {certifications.sections.map((section, sIdx) => (
              <div key={section.title} className="space-y-3">
                <div className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-fg-faint opacity-60" />
                  <h4 className="text-xs font-mono tracking-wider text-fg-muted uppercase">
                    {section.title}
                  </h4>
                  <div className="flex-1 h-px bg-surface/[0.06]" />
                </div>
                <motion.div
                  variants={staggerContainer(0.04)}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true }}
                  className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-1"
                >
                  {section.certifications.map((cert, i) => (
                    <CertBadge
                      key={cert.title}
                      cert={cert}
                      index={sIdx * 4 + i}
                    />
                  ))}
                </motion.div>
              </div>
            ))}
          </div>
        ) : (
          <motion.div
            variants={staggerContainer(0.05)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3"
          >
            {certifications.certifications.map((cert, i) => (
              <CertBadge key={cert.title} cert={cert} index={i} />
            ))}
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
}

export default Education;
